import TileLayer from 'ol/layer/Tile';
import ImageStatic from 'ol/source/ImageStatic';
import XYZ from 'ol/source/XYZ';
import ImageLayer from 'ol/layer/Image';
import Select from 'ol/interaction/Select';
import Collection from 'ol/Collection';
import View from 'ol/View';
import Kinetic from 'ol/Kinetic';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import DragPan from 'ol/interaction/DragPan';
import KeyboardPan from 'ol/interaction/KeyboardPan';
import KeyboardZoom from 'ol/interaction/KeyboardZoom';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import PinchZoom from 'ol/interaction/PinchZoom';
import OlMap from 'ol/Map';
import * as olCondition from 'ol/events/condition';

import getMinZoomFittingContentInView from '@mapsight/lib-ol/view/getMinZoomFittingContentInView';
import detectFeatureHits from '@mapsight/lib-ol/feature/detectFeatureHits';

import mapActions from '../actions/map-actions';
import {
	contentExtentHeight,
	contentExtentWidth,
	extent,
	minZoom,
	tileLayerPreloadNumber,
	tileLayerSourceOptions,
	debugOverlayLayerSourceOptions,
	imageOverlayLayerSourceOptions,
} from '../config';

import tooltipFactory from './modules/tooltip';
import dataLayerFactory from './modules/data-layer';
import geolocationFactory from './modules/geolocation';
import legendFactory from './modules/legend';
//import arrowLayerFactory from './modules/arrow-layer';
import {addZoomClassToViewport} from './modules/zoom-class';
import InteractionDisabler from './modules/interaction-disabler';
import overlays from './modules/overlays';
import uiIntegration from './modules/ui-integration';

import styleFunctionSelected from './styles/selected';
import {setupDebug} from './debug';

export default function renderMap(config) {
	//const arrowLayer = arrowLayerFactory(config);
	const dataVectorLayer = dataLayerFactory(config);
	const tileLayer = new TileLayer({
		preload: tileLayerPreloadNumber,
		extent: extent,
		source: new XYZ(tileLayerSourceOptions),
	});

	const selectClick = new Select({
		layers: [dataVectorLayer],
		style: styleFunctionSelected,
		condition: olCondition.click,
	});

	const mapElement = document.querySelector('#bssp-map');
	const exactMinZoom = getMinZoomFittingContentInView(
		contentExtentWidth,
		contentExtentHeight,
		mapElement.offsetWidth,
		mapElement.offsetHeight
	);

	const layers = [
		tileLayer,
		//new ol.layer.Tile({source: new ol.source.OSM()}),
	];

	if (imageOverlayLayerSourceOptions) {
		layers.push(
			new ImageLayer({
				extent: config.extent,
				source: new ImageStatic(imageOverlayLayerSourceOptions),
			})
		);
	}

	if (debugOverlayLayerSourceOptions) {
		layers.push(
			new ImageLayer({
				source: new ImageStatic(debugOverlayLayerSourceOptions),
				opacity: 0.5,
			})
		);
	}

	//layers.push(arrowLayer);
	layers.push(dataVectorLayer);

	const map = new OlMap({
		layers: layers,
		target: mapElement,
		interactions: new Collection([
			new DragPan({kinetic: new Kinetic(-0.005, 0.05, 100)}),
			new PinchZoom(),
			new KeyboardPan(),
			new KeyboardZoom(),
			new MouseWheelZoom(),
		]),
		controls: new Collection([]),
		view: new View({
			center: config.center,
			zoom: Math.floor(exactMinZoom),
			maxZoom: config.maxZoom,
			minZoom: minZoom,
			extent: config.extent,
		}),
	});

	if (config.enableDataEditing) {
		setupDebug(dataVectorLayer, map); // arrowLayer
	} else {
		map.addInteraction(new DoubleClickZoom());
		map.addInteraction(selectClick);
	}

	if (!config.enableDataEditing && !('ontouchstart' in window)) {
		// tooltips
		const {tooltip: overlay, selectHover} = tooltipFactory(
			[dataVectorLayer],
			styleFunctionSelected
		);
		map.addOverlay(overlay);
		map.getInteractions().push(selectHover);

		// feature hover
		detectFeatureHits(map, {
			layers: [dataVectorLayer],
			onEnter: (feature) => mapActions.featureHit(feature),
			onLeave: (feature) => mapActions.featureUnhit(feature),
		});
	}

	addZoomClassToViewport(map);
	overlays(map);

	uiIntegration(map, selectClick, dataVectorLayer.getSource());

	const {geolocationVectorLayer, geolocationControl} =
		geolocationFactory('EPSG:900913');
	map.getLayers().push(geolocationVectorLayer);
	map.getControls().push(geolocationControl);

	const {legendControl} = legendFactory();
	map.getControls().push(legendControl);

	if (config.isSplitView) {
		const interactionDisabler = new InteractionDisabler(
			map.getInteractions(),
			[selectClick]
		);

		mapActions.disable.listen(() => interactionDisabler.disable());
		mapActions.enable.listen(() => interactionDisabler.enable());
	}

	if (config.enableTilesConsoleDump) {
		const tileGrid = tileLayer.getSource().getTileGrid();
		const tileUrls = [];
		for (let z = config.minZoom; z <= config.maxZoom; z++) {
			const leftBottom = tileGrid.getTileCoordForCoordAndZ(
				[config.extent[0], config.extent[1]],
				z
			);
			const rightTop = tileGrid.getTileCoordForCoordAndZ(
				[config.extent[2], config.extent[3]],
				z,
				{reverseIntersectionPolicy: true}
			);

			const left = leftBottom[1];
			const bottom = -leftBottom[2];

			const right = rightTop[1];
			const top = -rightTop[2] - 1;

			for (let x = left; x <= right; x++) {
				for (let y = top; y <= bottom; y++) {
					tileUrls.push([z, x, y]);
				}
			}
		}

		console.log(
			tileUrls
				.map(
					(zxy) =>
						`wget "$headers" 'https://api.mapbox.com/styles/v1/neonaut/cla89s75i001k14sa19gr353j/tiles/256/${zxy.join(
							'/'
						)}?access_token=pk.eyJ1IjoibmVvbmF1dCIsImEiOiJjaWY2bXgwNTAwMDdodGltNHZpdGZ5N25yIn0.vRh5xBPnjmsARhQTuLvSUQ' -O ./tiles/${zxy.join(
							'_'
						)}.png`
				)
				.join('\n')
		);
	}
}
